import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };

const COLS = [
  { key:'client_name',   label:'Client' },
  { key:'medication_name',label:'Medication' },
  { key:'dosage',        label:'Dosage' },
  { key:'form',          label:'Form' },
  { key:'frequency',     label:'Frequency' },
  { key:'route',         label:'Route' },
  { key:'start_date',    label:'Start Date',  render: fmtDate },
  { key:'end_date',      label:'End Date',    render: v => v ? fmtDate(v) : <span className="text-green-600 text-xs">Ongoing</span> },
  { key:'instructions',  label:'Instructions',render: v => v ? <span className="text-xs text-slate-500 max-w-[200px] truncate block">{v}</span> : '–' },
];

export default function MedicationUsage() {
  const [data, setData]         = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId] = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients||[])).catch(()=>{});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (clientId) p.set('client_id', clientId);
    api.get(`/reports/medication-usage?${p}`)
      .then(r => { setData(r.data||[]); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, clientId]);

  useEffect(() => { load(); }, [load]);

  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    { label:'Date Range', value:`${startDate} - ${endDate}` },
    ...(selClient ? [{ label:'Client', value:`${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
  ];

  return (
    <ReportLayout title="Medication Usage Report" data={data} filename="medication_usage"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
